/**
 * Mobile menu for the topbar: left drawer (seller-style) with dimmed backdrop.
 */
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { pathByRouteName } from '../../../../util/routes';
import { ensureCurrentUser } from '../../../../util/data';
import {
  SELLER_DASHBOARD_MAIN_NAV_ITEMS,
  SELLER_SETTINGS_NAV_PARENT,
  SellerNavIconLogout,
} from '../../../../util/sellerNavItems';
import {
  CUSTOMER_DASHBOARD_MAIN_NAV_ITEMS,
  CUSTOMER_SETTINGS_NAV_PARENT,
  CustomerNavIconLogout,
} from '../../../../util/customerNavItems';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import LayoutWrapperAccountSettingsSideNav from '../../../../components/LayoutComposer/LayoutSideNavigation/LayoutWrapperAccountSettingsSideNav';
import layoutSideNavCss from '../../../../components/LayoutComposer/LayoutSideNavigation/LayoutSideNavigation.module.css';

import {
  TopbarIconAccountSettings,
  TopbarIconDashboard,
  TopbarIconInbox,
  TopbarIconListings,
  TopbarIconLogout,
  TopbarIconProfileSettings,
} from '../TopbarLinkIcons';

import css from './TopbarMobileMenu.module.css';

const MOBILE_TOPBAR_ICON_SIZE = 22;

const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CustomLinkComponent = ({ linkConfig, currentPage, onNavigate }) => {
  const { text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  if (type === 'internal' && route) {
    const { name, params, to } = route || {};
    const className = classNames(css.sellerNavLink, getCurrentPageClass(name));
    return (
      <li className={css.drawerListItem}>
        <NamedLink name={name} params={params} to={to} className={className} onClick={onNavigate}>
          {text}
        </NamedLink>
      </li>
    );
  }
  return (
    <li className={css.drawerListItem}>
      <ExternalLink href={href} className={css.sellerNavLink} onClick={onNavigate}>
        {text}
      </ExternalLink>
    </li>
  );
};

/**
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {Object?} props.currentUser
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks
 * @param {boolean} props.showSellerDashboardLink
 * @param {boolean} props.showCustomerDashboardLink
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @param {Function} props.onLogout
 * @param {Function} props.onCloseMenu — close the mobile menu layer (e.g. clear URL modal state)
 * @param {string} props.marketplaceName
 * @param {Object?} props.accountSettingsNavProps — seller hub: embedded account nav (see `getSellerEmbeddedAccountNavProps`)
 * @param {Object?} props.customerAccountSettingsNavProps — customer hub: embedded account nav
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
    showSellerDashboardLink = false,
    showCustomerDashboardLink = false,
    onCloseMenu,
    marketplaceName,
    accountSettingsNavProps,
    customerAccountSettingsNavProps,
  } = props;

  const intl = useIntl();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const user = ensureCurrentUser(currentUser);

  const orderNotificationsCount = useSelector(
    state => state.user?.currentUserOrderNotificationCount ?? 0
  );
  const saleNotificationsCount = useSelector(
    state => state.user?.currentUserSaleNotificationCount ?? 0
  );

  const hubAccountSettingsNavProps = showSellerDashboardLink
    ? accountSettingsNavProps
    : customerAccountSettingsNavProps;
  const accountPageForNav = hubAccountSettingsNavProps?.currentPage;
  const isAccountSection =
    !!accountPageForNav && ACCOUNT_SETTINGS_PAGES.includes(accountPageForNav);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(isAccountSection);

  useEffect(() => {
    if (!isAccountSection) {
      setSettingsMenuOpen(false);
    }
  }, [accountPageForNav, isAccountSection]);

  const hubDashboardRouteName = showSellerDashboardLink
    ? 'SellerDashboardPage'
    : showCustomerDashboardLink
    ? 'CustomerDashboardPage'
    : null;
  const overviewPath = hubDashboardRouteName
    ? pathByRouteName(hubDashboardRouteName, routeConfiguration, {})
    : '';
  const isPathActive = (routeName, params = {}) => {
    const p = pathByRouteName(routeName, routeConfiguration, params);
    return location.pathname === p;
  };

  const inboxNotificationCountForItem = ({ routeName, params }) => {
    if (routeName !== 'InboxPage') {
      return 0;
    }
    if (params?.tab === 'orders') {
      return orderNotificationsCount;
    }
    if (params?.tab === 'sales') {
      return saleNotificationsCount;
    }
    return 0;
  };

  const renderHubNavLink = (item, dashboardRouteName) => {
    const { routeName, params, messageId, Icon } = item;
    const pathMatches = isPathActive(routeName, params);
    const active =
      routeName === dashboardRouteName ? location.pathname === overviewPath : pathMatches;

    const notificationCountForLink = inboxNotificationCountForItem(item);

    return (
      <NamedLink
        key={messageId}
        name={routeName}
        params={params}
        className={classNames(css.sellerNavLink, { [css.sellerNavLinkActive]: active })}
        onClick={onCloseMenu}
      >
        <span className={css.sellerNavIcon}>
          <Icon />
        </span>
        <span className={css.sellerNavLinkLabel}>
          <FormattedMessage id={messageId} />
        </span>
        {notificationCountForLink > 0 ? (
          <NotificationBadge
            className={css.sellerNavNotificationBadge}
            count={notificationCountForLink}
          />
        ) : null}
      </NamedLink>
    );
  };

  const settingsExpanded = isAccountSection || settingsMenuOpen;
  const toggleSettingsMenu = () => setSettingsMenuOpen(v => !v);

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
        onNavigate={onCloseMenu}
      />
    );
  });

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink
      className={css.createNewListingLink}
      name="NewListingPage"
      onClick={onCloseMenu}
    >
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.authLink} onClick={onCloseMenu}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.authLink} onClick={onCloseMenu}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage
          id="TopbarMobileMenu.signupOrLogin"
          values={{ lineBreak: <br />, signup, login }}
        />
      </span>
    );

    return (
      <div className={css.drawerRoot}>
        <header className={css.drawerHeader}>
          <p className={css.drawerBrand}>{marketplaceName}</p>
          <button
            type="button"
            className={css.drawerClose}
            onClick={onCloseMenu}
            aria-label={intl.formatMessage({ id: 'TopbarMobileMenu.closeDrawer' })}
          >
            <span aria-hidden="true" className={css.drawerCloseMark}>
              ×
            </span>
          </button>
        </header>
        <div className={css.drawerBody}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>
          <ul className={css.customLinksList}>{extraLinks}</ul>
        </div>
        <div className={css.drawerFooter}>{createListingsLinkMaybe}</div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.sellerNavLinkActive : null;
  };

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink
      name="ManageListingsPage"
      className={classNames(css.sellerNavLink, currentPageClass('ManageListingsPage'))}
      onClick={onCloseMenu}
    >
      <span className={css.sellerNavIcon}>
        <TopbarIconListings size={MOBILE_TOPBAR_ICON_SIZE} />
      </span>
      <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
    </NamedLink>
  ) : null;

  const dashboardLinkMaybe =
    showSellerDashboardLink || showCustomerDashboardLink ? (
      <NamedLink
        name={showSellerDashboardLink ? 'SellerDashboardPage' : 'CustomerDashboardPage'}
        className={classNames(css.sellerNavLink, {
          [css.sellerNavLinkActive]:
            currentPage === 'SellerDashboardPage' || currentPage === 'CustomerDashboardPage',
        })}
        onClick={onCloseMenu}
      >
        <span className={css.sellerNavIcon}>
          <TopbarIconDashboard size={MOBILE_TOPBAR_ICON_SIZE} />
        </span>
        <FormattedMessage id="TopbarMobileMenu.dashboardLink" />
      </NamedLink>
    ) : null;

  const standardAccountNav = (
    <>
      {dashboardLinkMaybe}
      <NamedLink
        name="InboxPage"
        params={{ tab: inboxTab }}
        className={classNames(
          css.sellerNavLink,
          currentPageClass(`InboxPage:${inboxTab}`)
        )}
        onClick={onCloseMenu}
      >
        <span className={css.sellerNavIcon}>
          <TopbarIconInbox size={MOBILE_TOPBAR_ICON_SIZE} />
        </span>
        <span className={css.inboxLinkLabel}>
          <FormattedMessage id="TopbarMobileMenu.inboxLink" />
          {notificationCountBadge}
        </span>
      </NamedLink>
      {manageListingsLinkMaybe}
      <NamedLink
        name="ProfileSettingsPage"
        className={classNames(css.sellerNavLink, currentPageClass('ProfileSettingsPage'))}
        onClick={onCloseMenu}
      >
        <span className={css.sellerNavIcon}>
          <TopbarIconProfileSettings size={MOBILE_TOPBAR_ICON_SIZE} />
        </span>
        <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
      </NamedLink>
      <NamedLink
        name="AccountSettingsPage"
        className={classNames(css.sellerNavLink, currentPageClass('AccountSettingsPage'))}
        onClick={onCloseMenu}
      >
        <span className={css.sellerNavIcon}>
          <TopbarIconAccountSettings size={MOBILE_TOPBAR_ICON_SIZE} />
        </span>
        <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
      </NamedLink>
    </>
  );

  return (
    <div className={css.drawerRoot}>
      <header className={css.drawerHeader}>
        <p className={css.drawerBrand}>{marketplaceName}</p>
        <button
          type="button"
          className={css.drawerClose}
          onClick={onCloseMenu}
          aria-label={intl.formatMessage({ id: 'TopbarMobileMenu.closeDrawer' })}
        >
          <span aria-hidden="true" className={css.drawerCloseMark}>
            ×
          </span>
        </button>
      </header>

      <div className={css.drawerBody}>
        <div className={css.userRow}>
          <AvatarLarge className={css.avatar} user={currentUser} />
          <div className={css.userText}>
            <span className={css.greeting}>
              <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
            </span>
            {!showSellerDashboardLink && !showCustomerDashboardLink ? (
              <InlineTextButton
                rootClassName={css.logoutButton}
                onClick={() => {
                  onCloseMenu();
                  onLogout();
                }}
              >
                <span className={css.logoutRow}>
                  <span className={css.sellerNavIcon}>
                    <TopbarIconLogout size={MOBILE_TOPBAR_ICON_SIZE} />
                  </span>
                  <FormattedMessage id="TopbarMobileMenu.logoutLink" />
                </span>
              </InlineTextButton>
            ) : null}
          </div>
        </div>

        <nav
          className={css.drawerNav}
          aria-label={intl.formatMessage({
            id: showSellerDashboardLink
              ? 'SellerDashboard.sidebarAria'
              : showCustomerDashboardLink
              ? 'CustomerDashboard.sidebarAria'
              : 'TopbarMobileMenu.navAria',
          })}
        >
          {showSellerDashboardLink && accountSettingsNavProps ? (
            <>
              {SELLER_DASHBOARD_MAIN_NAV_ITEMS.map(item =>
                renderHubNavLink(item, 'SellerDashboardPage')
              )}
              <button
                type="button"
                id="seller-hub-settings-trigger"
                className={classNames(css.sellerNavLink, css.sellerNavSettingsTrigger, {
                  [css.sellerNavLinkActive]: isAccountSection,
                })}
                aria-expanded={settingsExpanded}
                aria-controls="seller-hub-settings-submenu"
                onClick={toggleSettingsMenu}
              >
                <span className={css.sellerNavIcon}>
                  <SELLER_SETTINGS_NAV_PARENT.Icon />
                </span>
                <span className={css.sellerNavLinkLabel}>
                  <FormattedMessage id={SELLER_SETTINGS_NAV_PARENT.messageId} />
                </span>
                <span
                  className={classNames(css.sellerNavChevron, {
                    [css.sellerNavChevronOpen]: settingsExpanded,
                  })}
                >
                  <ChevronDown />
                </span>
              </button>
              {settingsExpanded ? (
                <div id="seller-hub-settings-submenu" className={layoutSideNavCss.sellerNavSubmenu}>
                  <LayoutWrapperAccountSettingsSideNav
                    accountSettingsNavProps={accountSettingsNavProps}
                    ariaLabel={intl.formatMessage({
                      id: 'LayoutSideNavigation.screenreader.accountNavigation',
                    })}
                    variant="embedded"
                    onLinkClick={onCloseMenu}
                  />
                </div>
              ) : null}
            </>
          ) : showCustomerDashboardLink && customerAccountSettingsNavProps ? (
            <>
              {CUSTOMER_DASHBOARD_MAIN_NAV_ITEMS.map(item =>
                renderHubNavLink(item, 'CustomerDashboardPage')
              )}
              <button
                type="button"
                id="customer-hub-settings-trigger"
                className={classNames(css.sellerNavLink, css.sellerNavSettingsTrigger, {
                  [css.sellerNavLinkActive]: isAccountSection,
                })}
                aria-expanded={settingsExpanded}
                aria-controls="customer-hub-settings-submenu"
                onClick={toggleSettingsMenu}
              >
                <span className={css.sellerNavIcon}>
                  <CUSTOMER_SETTINGS_NAV_PARENT.Icon />
                </span>
                <span className={css.sellerNavLinkLabel}>
                  <FormattedMessage id={CUSTOMER_SETTINGS_NAV_PARENT.messageId} />
                </span>
                <span
                  className={classNames(css.sellerNavChevron, {
                    [css.sellerNavChevronOpen]: settingsExpanded,
                  })}
                >
                  <ChevronDown />
                </span>
              </button>
              {settingsExpanded ? (
                <div id="customer-hub-settings-submenu" className={layoutSideNavCss.sellerNavSubmenu}>
                  <LayoutWrapperAccountSettingsSideNav
                    accountSettingsNavProps={customerAccountSettingsNavProps}
                    ariaLabel={intl.formatMessage({
                      id: 'LayoutSideNavigation.screenreader.accountNavigation',
                    })}
                    variant="embedded"
                    onLinkClick={onCloseMenu}
                  />
                </div>
              ) : null}
            </>
          ) : (
            standardAccountNav
          )}
        </nav>

        {showSellerDashboardLink || showCustomerDashboardLink ? (
          <div className={css.sellerDrawerFooter}>
            <button
              type="button"
              className={css.sellerNavLogoutBtn}
              onClick={() => {
                onCloseMenu();
                onLogout();
              }}
            >
              <span className={css.sellerNavIcon}>
                {showSellerDashboardLink ? <SellerNavIconLogout /> : <CustomerNavIconLogout />}
              </span>
              <FormattedMessage
                id={showSellerDashboardLink ? 'SellerDashboard.logout' : 'CustomerDashboard.logout'}
              />
            </button>
          </div>
        ) : null}

        <ul className={css.customLinksList}>{extraLinks}</ul>
      </div>

      <div className={css.drawerFooter}>{createListingsLinkMaybe}</div>
    </div>
  );
};

export default TopbarMobileMenu;
